// Pure state + request-body logic of legacy `V3CoinSettingsPopupComponent`
// (`refreshData()` initialisation and `save()` body). BUSINESS-CRITICAL:
// this is the body spotbot applies to a live coin pair, so every rule below
// is a verbatim port and unit tested (`coinSettingsForm.test.ts`).

import type { CoinConfig, ConstantEntry } from '../../api/tradingTypes'

export interface ConstantOverride {
  /** -2 trader's value, -1 my default, -100 custom (then `customValue`). */
  value: number
  customValue: number | string
}

export interface CoinSettingsForm {
  syncState: number
  tradingModeValue: number | string
  bid: number | string
  bidConstant: string
  gridUp: number | string
  gridUpConstant: string
  gridDown: number | string
  gridDownConstant: string
  setDifferentGridDown: boolean
  lockOnOffMode: number | string
  maxMopSelectValue: number
  maxMopCustomValue: number | string
  /** Derived UI group of `offModeTrigger` (see `offHandlerTypeOf`). */
  offHandlerType: number
  offModeTrigger: number | string
  offHandlerRunOnNonSameStrategyPositions: number | string
  autoBuyMode: number | string
  autoBuyAmountSelectValue: number
  autoBuyAmountCustomValue: number | string
  autoBuyUsdAmountSelectValue: number
  autoBuyUsdAmountCustomValue: number | string
  autoBuyAsset: string
  canCloseAllMop: unknown
  canBuyMultipleOnSameGrid: unknown
  calculateAvgOnSell: unknown
  bidConstantNames: string[]
  bidConstants: Record<string, ConstantOverride>
  gridConstantNames: string[]
  gridConstants: Record<string, ConstantOverride>
}

const n = (v: unknown, fallback = 0): number => {
  const x = Number(v)
  return Number.isFinite(x) ? x : fallback
}

/** `offModeTrigger < 1` passes through; 1..9 is group 1 (sell open
 * positions); 10..19 is group 2 (MirrorTrading). */
export function offHandlerTypeOf(offModeTrigger: unknown): number {
  const t = n(offModeTrigger)
  if (t < 1) return t
  if (t < 10) return 1
  if (t < 20) return 2
  return t
}

/** `offHandlerTypeChanged()`: the group select resets the trigger to the
 * group's first option. */
export function offModeTriggerForType(type: number): number {
  if (type < 1) return type
  if (type === 1) return 1
  return 11
}

function seedConstants(
  effective: ConstantEntry[] | undefined,
  own: ConstantEntry[] | undefined,
  syncState: number,
): { names: string[]; map: Record<string, ConstantOverride> } {
  const names: string[] = []
  const map: Record<string, ConstantOverride> = {}
  for (const constant of Array.isArray(effective) ? effective : []) {
    const key = String(constant.key)
    map[key] = { value: syncState === 0 ? -1 : -2, customValue: constant.value }
    names.push(key)
  }
  for (const constant of Array.isArray(own) ? own : []) {
    const key = String(constant.key)
    // Legacy assigned into `bidConstants[key]` and would throw for a key
    // missing from `effective`; the backend's effective list is a superset.
    if (!map[key]) continue
    map[key].value = n(constant.value)
    if (parseFloat(`${constant.value}`) >= 0) map[key].customValue = constant.value
  }
  for (const name of names) {
    const entry = map[name]
    if (entry && entry.value >= 0) entry.value = -100
  }
  return { names, map }
}

/** `refreshData()` of the settings popup. */
export function initCoinSettingsForm(config: CoinConfig): CoinSettingsForm {
  const syncState = n(config.syncState)
  const effective = config.effective ?? {}

  const maxMopCount = n(config.maxMopCount)
  let maxMopSelectValue = maxMopCount
  let maxMopCustomValue: number | string = 0
  if (maxMopCount <= 0) maxMopCustomValue = n(effective.maxMopCount)
  if (maxMopCount > 0) {
    maxMopSelectValue = -100
    maxMopCustomValue = maxMopCount
  }

  // JS semantics of the legacy comparisons: `null <= 0` and `null > -1` are
  // both true, i.e. a missing amount shows as "Custom value" with the
  // effective amount pre-filled.
  const autoBuyAmount = n(config.autoBuyAmount, 0)
  let autoBuyAmountSelectValue = autoBuyAmount
  let autoBuyAmountCustomValue: number | string = autoBuyAmount
  if (autoBuyAmount <= 0) autoBuyAmountCustomValue = n(effective.autoBuyAmount)
  if (autoBuyAmount > -1) autoBuyAmountSelectValue = -100

  const autoBuyUsdAmount = n(config.autoBuyUsdAmount, 0)
  let autoBuyUsdAmountSelectValue = autoBuyUsdAmount
  let autoBuyUsdAmountCustomValue: number | string = autoBuyUsdAmount
  if (autoBuyUsdAmount <= 0) autoBuyUsdAmountCustomValue = n(effective.autoBuyUsdAmount)
  if (autoBuyUsdAmount > -1) autoBuyUsdAmountSelectValue = -100

  const bid = seedConstants(effective.bidConstants, config.bidConstants, syncState)
  const grid = seedConstants(effective.gridConstants, config.gridConstants, syncState)

  return {
    syncState,
    tradingModeValue: n(config.tradingMode),
    bid: config.bid ?? '',
    bidConstant: config.bidConstant ?? '',
    gridUp: config.gridUp ?? '',
    gridUpConstant: config.gridUpConstant ?? '',
    gridDown: config.gridDown ?? '',
    gridDownConstant: config.gridDownConstant ?? '',
    setDifferentGridDown:
      String(config.gridUp ?? '') !== String(config.gridDown ?? '') ||
      String(config.gridUpConstant ?? '') !== String(config.gridDownConstant ?? ''),
    lockOnOffMode: config.lockOnOffMode ?? -1,
    maxMopSelectValue,
    maxMopCustomValue,
    offHandlerType: offHandlerTypeOf(config.offModeTrigger),
    offModeTrigger: n(config.offModeTrigger),
    offHandlerRunOnNonSameStrategyPositions: config.offHandlerRunOnNonSameStrategyPositions ?? -1,
    autoBuyMode: config.autoBuyMode ?? -1,
    autoBuyAmountSelectValue,
    autoBuyAmountCustomValue,
    autoBuyUsdAmountSelectValue,
    autoBuyUsdAmountCustomValue,
    autoBuyAsset: config.autoBuyAsset ?? '',
    canCloseAllMop: config.canCloseAllMop,
    canBuyMultipleOnSameGrid: config.canBuyMultipleOnSameGrid,
    calculateAvgOnSell: config.calculateAvgOnSell,
    bidConstantNames: bid.names,
    bidConstants: bid.map,
    gridConstantNames: grid.names,
    gridConstants: grid.map,
  }
}

/** `bidConstantChanged()` / `gridUpConstantChanged()` / `gridDownConstantChanged()`:
 * choosing a constant copies its effective value into the raw field (kept in
 * sync, not sent while the constant is chosen); "Fix value" restores the
 * effective raw value. */
export function valueForConstantChoice(
  constantKey: string,
  effectiveConstants: ConstantEntry[] | undefined,
  effectiveValue: unknown,
): number | string {
  if (constantKey) {
    const match = (effectiveConstants ?? []).find((c) => String(c.key) === constantKey)
    return match ? parseFloat(`${match.value}`) : ''
  }
  return (effectiveValue as number | string | undefined) ?? ''
}

/** `addNewBidConstant()` / `addNewGridConstant()` of the popup. */
export function addConstantOverride(
  names: string[],
  map: Record<string, ConstantOverride>,
  name: string,
  value: number,
): { names: string[]; map: Record<string, ConstantOverride> } | null {
  const key = name.trim().toUpperCase()
  if (!key || names.includes(key)) return null
  return { names: [...names, key], map: { ...map, [key]: { value: -100, customValue: value || 0 } } }
}

function constantBody(names: string[], map: Record<string, ConstantOverride>): ConstantEntry[] {
  return names.map((name) => {
    const entry = map[name] ?? { value: -1, customValue: 0 }
    return { key: name, value: entry.value === -100 ? entry.customValue : entry.value }
  })
}

/** `save()` — the exact PATCH body of
 * `PATCH /v3/spot/config/{ex}/coin/{c1}/{c2}`. Raw bid/grid/trading-mode/
 * lock fields go ONLY for an own config (`syncState == 0`); a subscribed
 * pair only sends constant overrides and the advanced fields. Without a
 * separate grid-down, grid-down is a copy of grid-UP. */
export function buildCoinSettingsBody(form: CoinSettingsForm): Record<string, unknown> {
  const body: Record<string, unknown> = {
    bidConstants: constantBody(form.bidConstantNames, form.bidConstants),
    gridConstants: constantBody(form.gridConstantNames, form.gridConstants),
    maxMopCount: form.maxMopSelectValue === -100 ? form.maxMopCustomValue : form.maxMopSelectValue,
    autoBuyMode: form.autoBuyMode,
    autoBuyAmount: form.autoBuyAmountSelectValue === -100 ? form.autoBuyAmountCustomValue : form.autoBuyAmountSelectValue,
    autoBuyUsdAmount:
      form.autoBuyUsdAmountSelectValue === -100 ? form.autoBuyUsdAmountCustomValue : form.autoBuyUsdAmountSelectValue,
    autoBuyAsset: form.autoBuyAsset,
    offModeTrigger: form.offModeTrigger,
    offHandlerRunOnNonSameStrategyPositions: form.offHandlerRunOnNonSameStrategyPositions,
    canCloseAllMop: form.canCloseAllMop,
    canBuyMultipleOnSameGrid: form.canBuyMultipleOnSameGrid,
    calculateAvgOnSell: form.calculateAvgOnSell,
  }
  if (form.syncState === 0) {
    body.bid = form.bidConstant ? -1 : form.bid
    body.bidConstant = form.bidConstant
    body.gridUp = form.gridUpConstant ? -1 : form.gridUp
    body.gridUpConstant = form.gridUpConstant
    if (form.setDifferentGridDown) {
      body.gridDown = form.gridDownConstant ? -1 : form.gridDown
      body.gridDownConstant = form.gridDownConstant
    } else {
      body.gridDown = form.gridUpConstant ? -1 : form.gridUp
      body.gridDownConstant = form.gridUpConstant
    }
    body.tradingMode = form.tradingModeValue
    body.lockOnOffMode = form.lockOnOffMode
  }
  return body
}

/** Bulk modify-by-tag (`V3CoinListSpotComponent.batchModifyCoinConfigs`). */
export interface BatchModifyState {
  acceptedFields: string[]
  setDifferentGridDown: boolean
  values: Record<string, unknown>
}

export const BATCH_FIELDS = [
  'tradingMode',
  'bid',
  'grid',
  'lockOnOffMode',
  'maxMopCount',
  'offModeTrigger',
  'offHandlerRunOnNonSameStrategyPositions',
  'autoBuyMode',
  'autoBuyAmount',
] as const

/** Exact pruning pipeline before `PATCH …/coin/bytag/{tagId}`. */
export function buildBatchModifyBody(state: BatchModifyState, primaryUsdAsset: string): Record<string, unknown> {
  const accepted = [...state.acceptedFields]
  const grid = accepted.includes('grid')
  const body: Record<string, unknown> = {}
  for (const [field, value] of Object.entries(state.values)) {
    const keep =
      accepted.includes(field) ||
      (field === 'bidConstant' && accepted.includes('bid')) ||
      ((field === 'gridUp' || field === 'gridUpConstant') && grid) ||
      ((field === 'gridDown' || field === 'gridDownConstant') && state.setDifferentGridDown && grid)
    if (!keep) continue
    if (value === null || value === undefined || value === '') continue
    body[field] = value
  }
  if (grid && !state.setDifferentGridDown) {
    if ('gridUp' in body) body.gridDown = body.gridUp
    else delete body.gridDown
    if ('gridUpConstant' in body) body.gridDownConstant = body.gridUpConstant
    else delete body.gridDownConstant
  }
  if (accepted.includes('autoBuyAmount') && body.autoBuyAmount !== undefined && body.autoBuyAmount !== null) {
    body.autoBuyAsset = primaryUsdAsset
    body.autoBuyUsdAmount = body.autoBuyAmount
    accepted.push('autoBuyAsset', 'autoBuyUsdAmount')
  }
  if (accepted.includes('bid')) accepted.push('bidConstant')
  if (grid) accepted.push('gridUp', 'gridUpConstant', 'gridDown', 'gridDownConstant')
  return { acceptedFields: accepted, ...body }
}
